import { DragEvent, FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { X } from "lucide-react";
import { CATEGORIES } from "@/constants/categories";
import { useInvalidateProducts, useProduct } from "@/hooks/useProducts";
import { createProduct, updateProduct, uploadProductImage } from "@/lib/api";
import { syncProductImages } from "@/lib/productImages";
import { getProductImages, type Product } from "@/types/product";

const emptyForm: Product = {
  id: "",
  name: "",
  categorySlug: CATEGORIES[0]?.slug ?? "uncategorized",
  brand: "",
  price: 0,
  unit: "",
  image: "",
  images: [],
};

const ACCEPTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

function buildProductId(name: string, brand: string, unique = false): string {
  const base = [slugify(brand), slugify(name)].filter(Boolean).join("-") || "product";
  if (!unique) return base.slice(0, 80);
  const suffix = Date.now().toString(36).slice(-4);
  return `${base}-${suffix}`.replace(/-+/g, "-").slice(0, 80);
}

function apiErrorDetail(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

export const AdminProductFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const invalidateProducts = useInvalidateProducts();
  const { data: existingProduct, isLoading, isError } = useProduct(isEdit ? id : undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<Product>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    completed: number;
    percent: number;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uploadLoadedRef = useRef<number[]>([]);

  const images = getProductImages(form);

  useEffect(() => {
    if (!existingProduct) return;
    const normalized = getProductImages(existingProduct);
    setForm({ ...existingProduct, ...syncProductImages(normalized) });
  }, [existingProduct]);

  useEffect(() => {
    if (isEdit && isError) setError("Product not found");
  }, [isEdit, isError]);

  const setField = <K extends keyof Product>(key: K, value: Product[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setImages = (next: string[]) => {
    setForm((prev) => ({ ...prev, ...syncProductImages(next) }));
  };

  const onNameChange = (name: string) => {
    setForm((prev) => {
      if (isEdit) return { ...prev, name };
      return {
        ...prev,
        name,
        id: buildProductId(name, prev.brand),
      };
    });
  };

  const onBrandChange = (brand: string) => {
    setForm((prev) => {
      if (isEdit) return { ...prev, brand };
      return {
        ...prev,
        brand,
        id: buildProductId(prev.name, brand),
      };
    });
  };

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;

    const invalid = list.find((f) => !ACCEPTED_IMAGE_TYPES.has(f.type));
    if (invalid) {
      setError("Only JPEG, PNG, WebP, or GIF images are allowed");
      return;
    }

    setError(null);
    setUploading(true);
    uploadLoadedRef.current = new Array(list.length).fill(0);
    setUploadProgress({ total: list.length, completed: 0, percent: 0 });

    const reportProgress = (completed: number) => {
      const totalBytes = list.reduce((sum, file) => sum + file.size, 0);
      const loaded = uploadLoadedRef.current.reduce((sum, bytes) => sum + bytes, 0);
      const percent =
        totalBytes > 0 ? Math.min(100, Math.round((loaded / totalBytes) * 100)) : 0;
      setUploadProgress({ total: list.length, completed, percent });
    };

    try {
      let completed = 0;
      const results = await Promise.all(
        list.map((file, index) =>
          uploadProductImage(file, ({ loaded, total }) => {
            const fileTotal = total && total > 0 ? total : file.size;
            uploadLoadedRef.current[index] = Math.min(loaded, fileTotal);
            reportProgress(completed);
          }).then((result) => {
            uploadLoadedRef.current[index] = file.size;
            completed += 1;
            reportProgress(completed);
            return result;
          })
        )
      );
      const urls = results.map((r) => r.url);
      setForm((prev) => ({ ...prev, ...syncProductImages([...getProductImages(prev), ...urls]) }));
    } catch (err: unknown) {
      setError(apiErrorDetail(err, "Image upload failed"));
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (url: string) => {
    setImages(images.filter((img) => img !== url));
  };

  const setCoverImage = (url: string) => {
    setImages([url, ...images.filter((img) => img !== url)]);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || submitting) return;
    setDragOver(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading || submitting || !e.dataTransfer.files?.length) return;
    void uploadFiles(e.dataTransfer.files);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEdit && !images.length) {
      setError("Upload at least one product image");
      return;
    }
    setError(null);
    setSubmitting(true);
    const payload = syncProductImages(images);
    try {
      if (isEdit && id) {
        await updateProduct(id, {
          name: form.name,
          categorySlug: form.categorySlug,
          brand: form.brand,
          price: form.price,
          unit: form.unit,
          ...payload,
        });
        await invalidateProducts(id);
      } else {
        const productId = buildProductId(form.name, form.brand, true);
        await createProduct({ ...form, id: productId, ...payload });
        await invalidateProducts();
      }
      navigate("/admin/products");
    } catch (err: unknown) {
      setError(apiErrorDetail(err, "Failed to save product"));
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && isLoading) {
    return <p className="text-sm text-vg-gray">Loading product…</p>;
  }

  if (isEdit && isError) {
    return (
      <div data-testid="admin-product-form-page">
        <Link to="/admin/products" className="text-sm text-vg-gray hover:text-vg-ink">
          ← Back to products
        </Link>
        <p className="text-sm text-red-600 mt-4">Product not found</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-product-form-page">
      <div className="mb-6">
        <Link to="/admin/products" className="text-sm text-vg-gray hover:text-vg-ink">
          ← Back to products
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight mt-2">
          {isEdit ? "Edit product" : "New product"}
        </h1>
      </div>

      <form onSubmit={onSubmit} className="max-w-xl bg-white border border-black/10 p-6 space-y-4">
        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Name</span>
          <input
            className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
            value={form.name}
            onChange={(e) => onNameChange(e.target.value)}
            required
            data-testid="admin-field-name"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">ID (slug)</span>
          <input
            className="w-full h-10 px-3 border border-black/15 bg-[#f3f4f6] text-sm text-vg-gray focus:outline-none cursor-default"
            value={form.id}
            readOnly
            required
            title="Auto-generated from brand and name"
            data-testid="admin-field-id"
          />
          {!isEdit && (
            <p className="text-xs text-vg-gray">Auto-generated from brand and name</p>
          )}
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Category</span>
          <select
            className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
            value={form.categorySlug}
            onChange={(e) => setField("categorySlug", e.target.value)}
            required
            data-testid="admin-field-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium">Brand</span>
          <input
            className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
            value={form.brand}
            onChange={(e) => onBrandChange(e.target.value)}
            required
            data-testid="admin-field-brand"
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Price (AUD)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
              value={form.price}
              onChange={(e) => setField("price", Number(e.target.value))}
              required
              data-testid="admin-field-price"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">
              Unit <span className="font-normal text-vg-gray">(optional)</span>
            </span>
            <input
              className="w-full h-10 px-3 border border-black/15 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-black"
              value={form.unit}
              onChange={(e) => setField("unit", e.target.value)}
              placeholder="e.g. 500ml"
              data-testid="admin-field-unit"
            />
          </label>
        </div>

        <div className="space-y-2" data-testid="admin-field-image">
          <span className="text-sm font-medium block">Product images</span>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!uploading && !submitting) fileInputRef.current?.click();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!uploading && !submitting) fileInputRef.current?.click();
              }
            }}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`border border-dashed p-4 transition-colors cursor-pointer ${
              dragOver
                ? "border-vg-navy bg-[#f3f4f6]"
                : "border-black/20 bg-[#f8fafc] hover:border-black/40"
            } ${uploading || submitting ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="admin-image-dropzone"
          >
            <p className="text-sm text-vg-ink">
              {uploading
                ? "Uploading to Cloudinary…"
                : dragOver
                  ? "Drop images to upload"
                  : "Drag & drop images here, or click to browse"}
            </p>
            <p className="text-xs text-vg-gray mt-1">
              JPEG, PNG, WebP, or GIF · max 8MB each · multiple files supported
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              disabled={uploading || submitting}
              onChange={(e) => void uploadFiles(e.target.files ?? [])}
              className="sr-only"
              data-testid="admin-image-file"
            />
          </div>

          {uploading && uploadProgress && (
            <div className="space-y-1.5 pt-1" data-testid="admin-image-upload-progress">
              <div className="flex items-center justify-between gap-3 text-xs text-vg-gray">
                <span>
                  Uploading {uploadProgress.completed} of {uploadProgress.total} file
                  {uploadProgress.total === 1 ? "" : "s"}
                </span>
                <span className="tabular-nums">{uploadProgress.percent}%</span>
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-black/10"
                role="progressbar"
                aria-valuenow={uploadProgress.percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Image upload progress"
              >
                <div
                  className="h-full rounded-full bg-vg-navy transition-[width] duration-150 ease-out"
                  style={{ width: `${uploadProgress.percent}%` }}
                />
              </div>
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 pt-2" data-testid="admin-image-gallery">
              {images.map((url, index) => (
                <div key={url} className="relative group border border-black/10 bg-white">
                  <img src={url} alt={`Product ${index + 1}`} className="w-full aspect-square object-cover" />
                  {index === 0 ? (
                    <span className="absolute top-1 left-1 bg-vg-navy text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5">
                      Cover
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCoverImage(url)}
                      className="absolute top-1 left-1 bg-white/90 text-[9px] uppercase tracking-wider px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Set cover
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(url)}
                    className="absolute top-1 right-1 p-0.5 bg-white/90 hover:bg-white text-vg-ink"
                    aria-label="Remove image"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600" data-testid="admin-form-error">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || uploading || (!isEdit && images.length === 0)}
            className="h-10 px-4 bg-vg-navy text-white text-sm font-medium hover:bg-vg-cyan disabled:opacity-50"
            data-testid="admin-form-submit"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create product"}
          </button>
          <Link
            to="/admin/products"
            className="inline-flex items-center h-10 px-4 border border-black/15 text-sm hover:bg-[#f8fafc]"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminProductFormPage;
